import React, { useState } from 'react';
import classNames from 'classnames';
import { Vehicle } from 'models/Vehicle';
import { Button } from 'ncoded-component-library';
import { useTranslation } from 'react-i18next';
import GraphTwoPropsComparison from 'components/GraphTwoPropsComparison';
import './GraphRepresentationVehicles.styles.scss';
import './GraphRepresentationVehicles.styles.responsive.scss';

type GraphRepresentationVehiclesProps = {
  className?: string;
  data: Vehicle[];
};

type PropTypeGraphPairs =
  | 'costComparison'
  | 'mileageComparison'
  | 'speedComparison'
  | 'fuelConsumption';

const availableGraphs: PropTypeGraphPairs[] = [
  'costComparison',
  'mileageComparison',
  'speedComparison',
  'fuelConsumption',
];

const PROPERTY_KEYS: Record<
  PropTypeGraphPairs,
  [keyof Vehicle, keyof Vehicle]
> = {
  mileageComparison: ['annualAgreedKilometers', 'annualForecastKilometers'],
  speedComparison: ['currentSpeed', 'averageSpeed'],
  costComparison: ['calculatedValueAtDate', 'actualValueAtDate'],
  fuelConsumption: ['avgFuelConsumption', 'avgFuelConsumptionWltp'],
};

const GRAPH_LEGENDS: Record<PropTypeGraphPairs, [string, string]> = {
  mileageComparison: ['distanceKm', 'vehiclePlate'],
  costComparison: ['valueInE', 'vehiclePlate'],
  speedComparison: ['speedInKmH', 'vehiclePlate'],
  fuelConsumption: ['fuelConsumptionVl', 'vehiclePlate'],
};

const LEGEND_KEYS: Record<PropTypeGraphPairs, Record<string, string>> = {
  fuelConsumption: {
    avgFuelConsumption: 'avgFC',
    avgFuelConsumptionWltp: 'avgFCwltp',
  },
  mileageComparison: {
    annualAgreedKilometers: 'agreed',
    annualForecastKilometers: 'forecast',
  },
  speedComparison: {
    currentSpeed: 'current',
    averageSpeed: 'average',
  },
  costComparison: {
    calculatedValueAtDate: 'expected',
    actualValueAtDate: 'actual',
  },
};

const GraphRepresentationVehicles: React.FC<
  GraphRepresentationVehiclesProps
> = (props) => {
  const { className, data } = props;
  const { t } = useTranslation();
  const [currentlySelectedPropsPair, setCurrentlySlectedPropPair] =
    useState<PropTypeGraphPairs>('costComparison');

  const classes = classNames('graph-representation-vehicles', className);

  return (
    <div className={classes}>
      <div className="graph-representation-vehicles__menu">
        {availableGraphs.map((pair) => (
          <Button
            key={pair}
            className={classNames({
              selected: pair === currentlySelectedPropsPair,
            })}
            onClick={() => {
              setCurrentlySlectedPropPair(pair);
            }}
          >
            {t(pair)}
          </Button>
        ))}
      </div>
      <GraphTwoPropsComparison
        items={data}
        keyPair={PROPERTY_KEYS[currentlySelectedPropsPair]}
        labelFormula={(item: Vehicle) => `${item.licensePlate}`}
        leftLegendString={t(GRAPH_LEGENDS[currentlySelectedPropsPair][0])}
        bottomLegendString={t(GRAPH_LEGENDS[currentlySelectedPropsPair][1])}
        mapKeyToLabel={LEGEND_KEYS[currentlySelectedPropsPair]}
      />
    </div>
  );
};

export default GraphRepresentationVehicles;
